import { CustomGradeCode } from '@/customGrading/constants/customGradeCodes';
import { InitialGradeSource } from '@/customGrading/entity/InitialGradeSource';
const LAB_COLOR_TO_REFERENCE_COLOR_MAP = {
    1: 28,
    2: 31,
    15: 1,
    16: 4,
    17: 7,
    18: 10,
    19: 13,
    20: 16,
    21: 19,
    22: 22,
    23: 25,
    24: 31,
    25: 31,
    26: 31,
    27: 31,
    28: 31,
    29: 31,
    30: 31,
};
export const METRIC_KEY_BY_CODE_MAP = {
    [CustomGradeCode.Color]: 'colorMetric',
    [CustomGradeCode.ColorViBox2]: 'pavillionColorViboxMetric',
    [CustomGradeCode.ColorViBox3]: 'pavillionColorViboxMetric',
    [CustomGradeCode.DeadZone]: 'deadZoneMetric',
};
export class CustomGradingService {
    static getDictByGradeId(gradeId, dict) {
        return dict.find(({ id }) => id === gradeId) || null;
    }
    static getCutwiseAutograde(code, product, references, dict) {
        const metricKey = METRIC_KEY_BY_CODE_MAP[code];
        const metric = product[metricKey];
        if (metric?.chroma?.grade) {
            /*
             * for more specific grade, we need to find to which subgrade metric value belongs to
             * the logic here is next:
             *  - "metric.chroma.position" is a value between 0 and 1
             *  - first, get array of group with subgrades, e.g. E+ E E-
             *  - second, get boundaries for array according to its length, e.g. grade D have only two elements: D D-, but grade E has three elements: E+ E E-
             *  - third, create array with boundaries, e.g. [0.333333, 0.666666, 1] for E+ E E- or [0.5, 1] for D D-
             *  - fourth, get index of boundaries related to "position" value, e.g. position 0.75 in array [0.333333, 0.666666, 1] will have index 2
             *  - fifth, having the index, get grade from subgrade array, for index 2 and array E+ E E- - subgrade is E-
             * */
            const dictEntryGroup = dict.filter(({ group }) => group === metric?.chroma?.grade);
            const part = 1 / dictEntryGroup.length;
            const boundaries = new Array(dictEntryGroup.length).fill(0).map((_, index) => part * (index + 1));
            const boundaryIndex = boundaries.findIndex((value) => metric?.chroma?.position <= value);
            const subDict = dictEntryGroup[boundaryIndex];
            if (subDict) {
                return new InitialGradeSource(subDict, metric);
            }
            const dictEntry = dict.find(({ title }) => title === metric?.chroma?.grade);
            if (dictEntry) {
                return new InitialGradeSource(dictEntry, metric);
            }
        }
        const grade = metric?.val ?? null;
        if (!grade) {
            return null;
        }
        const gradeValue = +grade;
        let referenceOnLeftSide = null;
        let referenceOnRightSide = null;
        for (let i = 0; i < dict.length; i++) {
            const reference = references.find((ref) => ref.id === dict[i].refId);
            const referenceValue = reference?.[metricKey]?.val;
            if (referenceValue) {
                const numericReferenceValue = +referenceValue;
                // If "gradeValue" is greater than current reference, this becomes "leftReference"
                if (gradeValue > numericReferenceValue) {
                    referenceOnLeftSide = reference;
                }
                else {
                    // If "gradeValue" is less than or equal to current reference, this becomes "rightReference"
                    referenceOnRightSide = reference;
                    break;
                }
            }
        }
        if (!referenceOnLeftSide) {
            return new InitialGradeSource(dict[0], metric);
        }
        if (!referenceOnRightSide) {
            return new InitialGradeSource(dict[dict.length - 1], metric);
        }
        const dictOnLeftSideIndex = dict.findIndex(({ refId }) => refId === referenceOnLeftSide.id);
        const dictOnRightSideIndex = dict.findIndex(({ refId }) => refId === referenceOnRightSide.id);
        if (dictOnLeftSideIndex === -1) {
            return new InitialGradeSource(dict[0], metric);
        }
        if (dictOnRightSideIndex === -1) {
            return new InitialGradeSource(dict[dict.length - 1], metric);
        }
        return new InitialGradeSource(dict[Math.floor((dictOnLeftSideIndex + dictOnRightSideIndex) / 2)], metric);
    }
    static getManualBracketing(code, product, dict) {
        const gradeId = product.customGrades?.[code]?.rawValue;
        if (gradeId) {
            const grade = CustomGradingService.getDictByGradeId(gradeId, dict);
            return grade ? new InitialGradeSource(grade) : null;
        }
        return null;
    }
    static getSupplierGrade(product, dict) {
        if (product.color?.id) {
            const grade = CustomGradingService.getDictByGradeId(LAB_COLOR_TO_REFERENCE_COLOR_MAP[product.color.id] ?? null, dict);
            return grade ? new InitialGradeSource(grade) : null;
        }
        return null;
    }
    static getInitialGrade(code, product, references, dict, basicDict) {
        // Priority 1 - check if grading diamond has "customGrades"
        const gradeByCustomGrade = CustomGradingService.getManualBracketing(code, product, dict);
        if (gradeByCustomGrade) {
            return gradeByCustomGrade;
        }
        // Priority 2 - check if grading diamond has color metric value
        const gradeByColorGrade = CustomGradingService.getSupplierGrade(product, dict);
        if (gradeByColorGrade) {
            return gradeByColorGrade;
        }
        // Priority 3 - check if grading diamond has metric value
        const gradeByMetricValue = CustomGradingService.getCutwiseAutograde(code, product, references, dict);
        if (gradeByMetricValue) {
            return gradeByMetricValue;
        }
        // Priority 4 - when no grade value found - put initial grade in the middle of the scale
        return new InitialGradeSource(basicDict[Math.ceil(basicDict.length / 2)]);
    }
    static getMetaBySourceType(type, product) {
        if (type === 'cutwise') {
            return {
                title: 'Cutwise Autograde',
                shortTitle: 'Autograde',
            };
        }
        if (type === 'supplier') {
            if (product.certifications && product.certifications.length > 0) {
                const baseCertificate = product.certifications[0];
                if (baseCertificate?.laboratory?.title) {
                    return {
                        title: `${baseCertificate.laboratory.title} grade`,
                        shortTitle: baseCertificate.laboratory.title,
                    };
                }
            }
            return {
                title: 'Supplier Grade',
                shortTitle: 'Supplier Grade',
            };
        }
        // by default - "manual" type
        return {
            title: 'Cutwise Manual Bracketing',
            shortTitle: 'Manual Bracketing',
        };
    }
}
